import React, {useEffect, useState, useRef} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import _, {size} from 'lodash';

import {getPercent} from '@ey/utils';
import {lightColor, darkColor} from '@ey/styled-theme';

import Utility from '../../../../util/utilityfunctions';
import {labels, currentResource} from '../../../../util/utils';
import {insightFilterId, ProfileDigitalGAMType, projectSessionKeys} from '../../../../util/uiconstants';
import {loadStorage, saveStorage} from '../../../Common/localStorage';

import BadgeLabel from '../../../Common/Badge/BadgeLabel';
import FilterChips from '../../../Common/FilterChips';
import Select from '../../../CoreWrappers/Select';

import MetricsTable from '../../../Common/MetricsTable';
import {strategyCustomizeIds} from '../../../Common/MetricsTable/columns';

const EngagementDetails = (props) => {
	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);
	const dashboardContext = useSelector(({dashboardContext}) => dashboardContext);
	const dashboardInsightFilters = useSelector(({dashboardFilters}) => dashboardFilters.insight);
	const hasFilters = dashboardInsightFilters !== null;

	const filters = [
		{id: insightFilterId.risks, label: labels.risks},
		{id: insightFilterId.strategy, label: labels.strategy},
		{id: insightFilterId.findings, label: labels.findingsLabel}
	];

	const themeColor = Utility.isDarkMode() ? darkColor : lightColor;

	const getGroupRoleTypeLabel = (typeId) => {
		let grouplabel = currentResource.gaRoleType.find((x) => typeId === x.id)?.label;
		if (grouplabel) {
			return grouplabel;
		}

		return labels.notApplicable;
	};

	const getDigitalAudit = (gamNumber) => {
		if (gamNumber === ProfileDigitalGAMType.yes) {
			return labels.yes;
		}

		if (gamNumber === ProfileDigitalGAMType.no) {
			return labels.no;
		}
	};

	const strategyCustomizeOptions = [
		{id: strategyCustomizeIds.digitalAuditId, label: labels.digitalAuditLabel},
		{id: strategyCustomizeIds.groupRoleId, label: labels.groupRole},
		{id: strategyCustomizeIds.substantiveId, label: labels.substantiveProcedures},
		{id: strategyCustomizeIds.controlsId, label: labels.controls},
		{id: strategyCustomizeIds.itId, label: labels.itlabel}
	];

	const strategyCustomizeInitial = [
		strategyCustomizeIds.digitalAuditId,
		strategyCustomizeIds.substantiveId,
		strategyCustomizeIds.controlsId
	];

	const [selectedFilter, setSelectedFilter] = useState(insightFilterId.risks);
	const [engagementData, setEngagementData] = useState(null);
	const [showMessage, setShowMessage] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [userChange, setUserChange] = useState(false);
	const openGroups = useRef([]);

	const maxColumnConfiguarable = 4;
	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.insightSession}`;

	const isStrategy = selectedFilter === insightFilterId.strategy;

	useEffect(() => {
		const currentState = loadStorage(psessionKEy);
		if (currentState && currentState.insightFilterid) {
			setSelectedFilter(currentState.insightFilterid);
		}
	}, []);

	useEffect(() => {
		buildTable();
	}, [props.engagementMetrics]);

	useEffect(() => {
		if (hasFilters) {
			const type = dashboardInsightFilters.filters[0].type;
			setSelectedFilter(type);
		} else setSelectedFilter(insightFilterId.risks);
		buildTable();
	}, [dashboardInsightFilters]);

	const getExportDetails = () => {
		return {
			title: filters.find((f) => f.id === selectedFilter).label,
			columns: getColumnsToExport(),
			data: getDataToExport(),
			worksheetName: filters.find((f) => f.id === selectedFilter).label
		};
	};

	const getColumnsToExport = () => {
		let columns = [];
		columns.push({header: labels.orgChartEntity, key: 'entityName'});
		columns.push({header: labels.engagementId, key: 'engId'});
		columns.push({header: labels.contactName, key: 'ContactName'});
		columns.push({header: labels.contactEmail, key: 'ContactEmail'});
		columns.push({header: labels.engagementLabel, key: 'engDes'});
		if (selectedFilter === insightFilterId.risks) {
			columns.push({header: labels.groupRole, key: 'groupRole'});
			columns.push({header: labels.totalRisks, key: 'totalRisks'});
			columns.push({header: labels.statusInherent, key: 'statusInherent'});
			columns.push({header: labels.statusSignificant, key: 'statusSignificant'});
			columns.push({header: labels.statusFraud, key: 'statusFraud'});
		} else if (isStrategy) {
			columns.push({header: labels.digitalAuditLabel, key: 'digitalAudit'});
			columns.push({header: labels.groupRole, key: 'groupRole'});
			columns.push({header: labels.substantivePSPs, key: 'substantivePSPs'});
			columns.push({header: labels.substantiveOSPs, key: 'substantiveOSPs'});
			columns.push({header: labels.substantiveHxPSPs, key: 'substantiveHxPSPs'});
			columns.push({header: labels.controlsTesting, key: 'controlsTesting'});
			columns.push({header: labels.controlsNonTesting, key: 'controlsNonTesting'});
			columns.push({header: labels.iTTesting, key: 'iTTesting'});
			columns.push({header: labels.iTNonTesting, key: 'iTNonTesting'});
		} else {
			columns.push({header: labels.groupRole, key: 'groupRole'});
			columns.push({header: labels.misstatementsFactual, key: 'misstatementsFactual'});
			columns.push({header: labels.misstatementsProjected, key: 'misstatementsProjected'});
			columns.push({header: labels.misstatementsJudgmental, key: 'misstatementsJudgmental'});
			columns.push({header: labels.misstatementsReclassification, key: 'misstatementsReclassification'});
			columns.push({header: labels.deficienciesDeficiency, key: 'deficienciesDeficiency'});
			columns.push({header: labels.deficienciesSignificantdeficiency, key: 'deficienciesSignificantdeficiency'});
			columns.push({header: labels.deficienciesMaterialweakness, key: 'deficienciesMaterialweakness'});
		}
		return columns;
	};

	const getDataToExport = () => {
		if (selectedFilter === insightFilterId.risks) {
			return getRisksDataToExport();
		} else if (isStrategy) {
			return getStrategyDataToExport();
		} else {
			return getFindingsToExport();
		}
	};

	const getRisksDataToExport = () => {
		let data = [];
		dashboardContext.orgChart.forEach((ochart) => {
			if (dashboardContext && _.size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = props.engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									groupRole: getGroupRoleTypeLabel(engMetricsData.gAType),
									totalRisks: engMetricsData.riskTotal,
									statusInherent: getNumberWithPercentage(engMetricsData.riskInherent, engMetricsData.riskTotal),
									statusSignificant: getNumberWithPercentage(engMetricsData.riskSigni, engMetricsData.riskTotal),
									statusFraud: getNumberWithPercentage(engMetricsData.riskFraud, engMetricsData.riskTotal)
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const getStrategyDataToExport = () => {
		let data = [];
		dashboardContext.orgChart.forEach((ochart) => {
			if (dashboardContext && _.size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = props.engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								let totalSubProcedures = engMetricsData.subPsp + engMetricsData.subOsp + engMetricsData.subHxPsp;
								let iTNonTesting =
									engMetricsData.itConTotal -
									engMetricsData.itConTested +
									(engMetricsData.itConSubTotal - engMetricsData.itConSubTested);
								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									groupRole: getGroupRoleTypeLabel(engMetricsData.gAType),
									digitalAudit: getDigitalAudit(engMetricsData.prDigtalGam),
									substantivePSPs: getNumberWithPercentage(engMetricsData.subPsp, totalSubProcedures),
									substantiveOSPs: getNumberWithPercentage(engMetricsData.subOsp, totalSubProcedures),
									substantiveHxPSPs: getNumberWithPercentage(engMetricsData.subHxPsp, totalSubProcedures),
									controlsTesting: getNumberWithPercentage(engMetricsData.conTested, engMetricsData.conTotal),
									controlsNonTesting: getNumberWithPercentage(
										engMetricsData.conTotal - engMetricsData.conTested,
										engMetricsData.conTotal
									),
									iTTesting: getNumberWithPercentage(
										engMetricsData.itConTested + engMetricsData.itConSubTested,
										engMetricsData.itConTotal + engMetricsData.itConSubTotal
									),
									iTNonTesting: getNumberWithPercentage(
										iTNonTesting,
										engMetricsData.itConTotal + engMetricsData.itConSubTotal
									)
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const getNumberWithPercentage = (partialValue, total) => {
		return (
			total &&
			partialValue &&
			`${partialValue} (${Utility.getFormattedNumber(numberFormat, getPercent(partialValue, total))}%)`
		);
	};

	const getFindingsToExport = () => {
		let data = [];
		dashboardContext.orgChart.forEach((ochart) => {
			if (dashboardContext && _.size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = props.engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								let misTotal =
									engMetricsData.misFactCY +
									engMetricsData.misProjCY +
									engMetricsData.misJudgCY +
									engMetricsData.misReclasCY;

								let defCount = engMetricsData.defDesign + engMetricsData.defOper + engMetricsData.defOperAndDes;
								let sDCount = engMetricsData.sDDesign + engMetricsData.sDOper + engMetricsData.sDOperAndDes;
								let mwCount = engMetricsData.mWDesign + engMetricsData.mWOper + engMetricsData.mWOperAndDes;
								let defTotal = defCount + sDCount + mwCount;

								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									groupRole: getGroupRoleTypeLabel(engMetricsData.gAType),
									misstatementsFactual: getNumberWithPercentage(engMetricsData.misFactCY, misTotal),
									misstatementsProjected: getNumberWithPercentage(engMetricsData.misProjCY, misTotal),
									misstatementsJudgmental: getNumberWithPercentage(engMetricsData.misJudgCY, misTotal),
									misstatementsReclassification: getNumberWithPercentage(engMetricsData.misReclasCY, misTotal),
									deficienciesDeficiency: getNumberWithPercentage(defCount, defTotal),
									deficienciesSignificantdeficiency: getNumberWithPercentage(sDCount, defTotal),
									deficienciesMaterialweakness: getNumberWithPercentage(mwCount, defTotal)
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const barStyling = {
		chartWidth: 440,
		// chartHeight: barItems1.length * 66,
		styleName: 'groupReportItem',
		chartWrapper: 'groupAuditchart'
	};

	const getChartData = (chartElements, total, customToolTip) => {
		let barItems = [];
		chartElements.forEach((item, index) => {
			let percentage = 0;
			if (item.data) {
				percentage = getPercent(item.data, total);
			}

			item.percentage = percentage;
			barItems.push({
				count: item.data,
				label: item.name,
				statusClass: '',
				percentage: percentage,
				color: item.color ? item.color : getSeriesColor(index),
				toolTip: `${item.name} | ${Utility.getFormattedNumber(numberFormat, item.data)} (${percentage}%)`
			});
		});

		updateChartSeries(barItems);
		const barChartData = {
			barStyling: barStyling,
			headerText: '',
			barMaxValue: total,
			showBarTitleTip: true,
			barItems: barItems,
			showCustomLegend: false,
			totalCount: total
		};

		return barChartData;
	};

	const getBarChartData = (chartElements, total) => {
		let barItems = [];
		chartElements.forEach((item, index) => {
			let percentage = getPercent(item.data, total);
			item.percentage = percentage;
			barItems.push({
				count: item.data,
				label: item.name,
				statusClass: '',
				color: item.color,
				toolTip: `<span>${item.name} | ${Utility.getFormattedNumber(
					numberFormat,
					item.data
				)} (${percentage}%)<span><br><span>${Utility.getFormattedNumber(numberFormat, item.control)} ${
					labels.controls
				}</span><br><span>${Utility.getFormattedNumber(numberFormat, item.subs)} ${labels.substantiveProcedures}</span>`
			});
		});

		return {
			barStyling: barStyling,
			headerText: '',
			barMaxValue: total,
			showBarTitleTip: true,
			barItems: barItems,
			showCustomLegend: false,
			totalCount: total
		};
	};

	const updateChartSeries = (series) => {
		Utility.adjustPercentages(series);

		for (let counter = 0; counter < series.length; counter++) {
			let barItem = series[counter];
			const formattedCount = Utility.getFormattedNumber(numberFormat, barItem.count);
			let toolTip = `${barItem.label} | ${formattedCount} (${barItem.percentage}%)`;
			barItem.toolTip = toolTip;
		}

		return series;
	};

	// Construct engagement table data from entities
	const buildTable = () => {
		// check if selected entities have engagements
		if (dashboardContext.entities?.every((entity) => entity.engagements?.length === 0)) {
			setShowMessage(true);
		} else {
			let data = [];
			// show entities base on org chart order and if it was selected
			dashboardContext.orgChart.forEach((node) => {
				loadNodes(node, dashboardContext.entities, data);
			});
			setShowMessage(false);
			// when searching, remove entity without engagement
			if (hasFilters) {
				data = data?.filter((x) => x?.items?.length !== 0);
			}
			setEngagementData(data);
		}
	};

	const loadNodes = (node, entities, data) => {
		// check if entity is selected
		let entity = entities.find((x) => x.id === node.id);

		if (entity && entity.engagements?.length > 0) {
			const items = getEngagements(entity);
			let group = {
				id: entity.id,
				name: entity.label,
				isOpen: isGroupOpened(entity.id),
				items,
				customGroupRender: () => {
					let result = <BadgeLabel label={entity.label} value={items.length} />;

					return result;
				}
			};

			group.engCount = group.items?.length;

			if (entity.isEnabled) {
				data.push(group);
			}
		}

		// load children entities for each node
		node.nodes.forEach((n) => {
			loadNodes(n, entities, data);
		});
	};

	// Get engagements for given entity
	const getEngagements = (entity) => {
		let engagements = entity.engagements.map((engagementId) => {
			return getEngagement(engagementId);
		});
		let areEngagements = engagements.filter((e) => e !== undefined);
		if (hasFilters) {
			areEngagements = areEngagements.filter(({engId}) => dashboardInsightFilters.engagements.includes(engId));
		}
		if (_.isEmpty(areEngagements)) {
			return [];
		} else {
			return areEngagements;
		}
	};

	const isGroupOpened = (id) => {
		return openGroups.current.some((x) => x === id);
	};

	// Get engagements details for given engagementId
	const getEngagement = (id) => {
		const item = props.engagementMetrics?.find((x) => x.engId === id);
		// Add some new fields to item as we need to do sorting on those values
		return item
			? {
					...item,
					pATOD: item.pAOpenOD + item.pAInProgOD,
					rATOD: item.rANotStartOD + item.rAInProgOD,
					rNOOD: item.rNOLowOD + item.rNOMedOD + item.rNOHighOD + item.rNOCritOD
			  }
			: item;
	};

	const getTableClass = () => {
		return ['risks-table', 'strategy-table', 'findings-table'][selectedFilter - 1];
	};

	// Show data for the selected toogle (Assignment/Notes)
	const handlerOnSwitchChange = (id) => {
		let _id = Number.parseInt(id);
		let currentState = loadStorage(psessionKEy);

		setSelectedFilter(_id);
		buildTable();

		if (!currentState) {
			currentState = {};
		}
		currentState.insightFilterid = _id;
		saveStorage(psessionKEy, currentState);
	};

	//TODO: refresh data if the accordion is open.
	const handlerOnGroupChange = (group, isOpen) => {
		if (isOpen) {
			openGroups.current.push(group.id);
		} else {
			openGroups.current = openGroups.current.filter((x) => x !== group.id);
		}
	};

	const renderCustomDetails = () => {
		if (dashboardInsightFilters?.filters) {
			const filterChipsProps = {
				filters: [...dashboardInsightFilters?.filters],
				onClearFilter: dashboardInsightFilters?.onClearFilter
			};

			return <FilterChips {...filterChipsProps} />;
		}
	};

	const renderTableSelect = () => {
		const selectProps = {
			value: selectedFilter,
			wrapperClass: 'table-select',
			options: filters,
			onChange: handlerOnSwitchChange
		};

		return <Select {...selectProps} />;
	};

	const renderTable = () => {
		const tableProps = {
			data: engagementData,
			variant: Object.keys(insightFilterId).find((key) => {
				if (insightFilterId[key] === selectedFilter) return key;
			}),
			renderHeader: (renderCustomizeColumns) => (
				<>
					{isStrategy && renderCustomizeColumns(labels.selectUpToFourOptionHeader)}
					{renderTableSelect()}
				</>
			),
			headerConfig: {
				$areas: 'search . customizeColumns tableSelect',
				$columns: 'var(--px-270) 1fr 1fr 1fr'
			},
			renderFilter: renderCustomDetails
		};

		if (isStrategy) {
			tableProps.columnVisibilityOptions = strategyCustomizeOptions;
			tableProps.columnVisibility = strategyCustomizeInitial;
			tableProps.maxCustomColumns = maxColumnConfiguarable;
		}

		return <MetricsTable {...tableProps} />;
	};

	return renderTable();
};

EngagementDetails.defaultProps = {
	engagementMetrics: []
};

EngagementDetails.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			engId: PropTypes.number,
			engKey: PropTypes.number
		})
	)
};

export default EngagementDetails;
